import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import ChecklistFormSlider from '../components/ChecklistFormSlider';
import { ListChecks, Plus, Search, Pencil, Trash2, Filter, Power } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Project {
  _id: string;
  name: string;
}

interface ChecklistItem {
  _id?: string;
  title: string;
  description: string;
  categoryLocationGroup: string;
  isRequired: boolean;
  order: number;
}

interface InspectionType {
  _id: string;
  name: string;
  order: number;
}

interface Checklist {
  _id: string;
  name: string;
  description: string;
  category: string;
  items: ChecklistItem[];
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

const Checklist: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    items: [] as ChecklistItem[]
  });

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadInspectionTypes();
      loadChecklists();
    }
  }, [selectedProjectId, selectedCategory, selectedStatus, searchQuery]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project
      if (projectsList.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsList[0]._id);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const loadInspectionTypes = async () => {
    if (!selectedProjectId) return;

    try {
      const response = await api.get(`/service-settings/${selectedProjectId}/inspection-types`);
      const types = response.data.items || [];
      setInspectionTypes(types);

      // Set default category to first inspection type if available
      if (types.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: types[0]._id }));
      }
    } catch (err: any) {
      console.error('Error loading inspection types:', err);
      toast.error('Failed to load inspection types');
    }
  };

  const loadChecklists = async () => {
    if (!selectedProjectId) return;

    try {
      const params: any = {
        includeInactive: true // Always include inactive checklists
      };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedStatus !== 'all') {
        params.isActive = selectedStatus === 'active';
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get(`/checklists/${selectedProjectId}`, { params });
      setChecklists(response.data.checklists);
    } catch (err: any) {
      console.error('Error fetching checklists:', err);
      toast.error('Failed to load checklists');
    }
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCreateChecklist = () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }
    setEditingChecklist(null);
    setFormData({
      name: '',
      description: '',
      category: 'pre-construction',
      items: []
    });
    setShowModal(true);
  };

  const handleEditChecklist = (checklist: Checklist) => {
    setEditingChecklist(checklist);
    setFormData({
      name: checklist.name,
      description: checklist.description,
      category: checklist.category,
      items: [...checklist.items]
    });
    setShowModal(true);
  };

  const handleDeleteClick = (checklistId: string) => {
    setChecklistToDelete(checklistId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!checklistToDelete) return;

    try {
      await api.delete(`/checklists/${selectedProjectId}/${checklistToDelete}`);
      toast.success('Checklist deleted successfully');
      setShowDeleteConfirm(false);
      setChecklistToDelete(null);
      loadChecklists();
    } catch (err: any) {
      console.error('Error deleting checklist:', err);
      toast.error('Failed to delete checklist');
    }
  };

  const handleToggleStatus = async (checklistId: string, currentStatus: boolean) => {
    try {
      await api.patch(`/checklists/${selectedProjectId}/${checklistId}/status`, {
        isActive: !currentStatus
      });
      toast.success(`Checklist ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadChecklists();
    } catch (err: any) {
      console.error('Error toggling checklist status:', err);
      toast.error('Failed to update checklist status');
    }
  };

  const handleSaveChecklist = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a checklist name');
      return;
    }

    if (!formData.category) {
      toast.error('Please select an inspection type');
      return;
    }

    // Check if any category has items but empty category name
    const categoriesWithItems = new Set<string>();
    formData.items.forEach(item => {
      categoriesWithItems.add(item.categoryLocationGroup);
    });

    if (categoriesWithItems.has('') && categoriesWithItems.size > 0) {
      toast.error('Please enter a name for all categories that have items');
      return;
    }

    try {
      if (editingChecklist) {
        // Update existing checklist
        await api.put(`/checklists/${selectedProjectId}/${editingChecklist._id}`, formData);
        toast.success('Checklist updated successfully');
      } else {
        // Create new checklist
        await api.post(`/checklists/${selectedProjectId}`, formData);
        toast.success('Checklist created successfully');
      }

      setShowModal(false);
      loadChecklists();
    } catch (err: any) {
      console.error('Error saving checklist:', err);
      toast.error('Failed to save checklist');
    }
  };

  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      title: '',
      description: '',
      categoryLocationGroup: '',
      isRequired: false,
      order: formData.items.length
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const handleUpdateItem = (index: number, field: keyof ChecklistItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    // Update order
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const getCategoryLabel = (categoryId: string) => {
    const type = inspectionTypes.find(t => t._id === categoryId);
    return type ? type.name : categoryId;
  };

  const getCategoryColor = (categoryId: string) => {
    // Generate consistent colors based on inspection type index
    const index = inspectionTypes.findIndex(t => t._id === categoryId);
    const colors = ['#48182F', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4'];
    return index >= 0 ? colors[index % colors.length] : '#6B7280';
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Page Header with Project Dropdown */}
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Checklist</h1>
              <p className="builders-section-subtitle">
                Create and manage service checklists
              </p>
            </div>
            <div style={{ marginTop: '4px' }}>
              <select
                className="form-input"
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={loading || projects.length === 0}
                style={{ minWidth: '280px' }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div className="builders-empty-state">
              <p>Please select a project to manage checklists</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              {/* Search, Filter and New Checklist Button */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search checklists..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="form-input"
                    style={{
                      width: '100%',
                      paddingLeft: '40px'
                    }}
                  />
                </div>

                {/* Category Filter */}
                <div style={{ position: 'relative', minWidth: '200px' }}>
                  <Filter
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF',
                      pointerEvents: 'none'
                    }}
                  />
                  <select
                    className="form-input"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{
                      paddingLeft: '40px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Categories</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div style={{ position: 'relative', minWidth: '150px' }}>
                  <Power
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF',
                      pointerEvents: 'none'
                    }}
                  />
                  <select
                    className="form-input"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'active' | 'inactive')}
                    style={{
                      paddingLeft: '40px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <button className="btn btn-primary btn-md" onClick={handleCreateChecklist}>
                  <Plus size={20} />
                  New Checklist
                </button>
              </div>

              {/* Checklists Table */}
              {checklists.length === 0 ? (
                <div className="data-table-container">
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6B7280'
                  }}>
                    <ListChecks size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      No checklists found
                    </p>
                    <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                      Get started by creating your first service checklist
                    </p>
                    <button className="btn btn-primary btn-md" onClick={handleCreateChecklist}>
                      <Plus size={20} />
                      New Checklist
                    </button>
                  </div>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Items</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklists.map((checklist) => (
                        <tr key={checklist._id}>
                          <td className="data-table-primary-cell">
                            {checklist.name}
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: `${getCategoryColor(checklist.category)}15`,
                              color: getCategoryColor(checklist.category)
                            }}>
                              {getCategoryLabel(checklist.category)}
                            </span>
                          </td>
                          <td className="data-table-secondary-cell">
                            {checklist.items.length}
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: (checklist.isActive ?? true) ? '#10B98115' : '#6B728015',
                              color: (checklist.isActive ?? true) ? '#10B981' : '#6B7280'
                            }}>
                              {(checklist.isActive ?? true) ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ width: '150px' }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStatus(checklist._id, checklist.isActive ?? true);
                                }}
                                title={(checklist.isActive ?? true) ? 'Deactivate' : 'Activate'}
                                style={{
                                  padding: '6px',
                                  color: (checklist.isActive ?? true) ? '#F59E0B' : '#10B981',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              >
                                <Power size={18} />
                              </button>
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditChecklist(checklist);
                                }}
                                title="Edit"
                                style={{
                                  padding: '6px',
                                  color: '#48182F',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              >
                                <Pencil size={18} />
                              </button>
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(checklist._id);
                                }}
                                title="Delete"
                                style={{
                                  padding: '6px',
                                  color: '#DC2626',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Checklist Slider */}
        <ChecklistFormSlider
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveChecklist}
          formData={formData}
          setFormData={setFormData}
          inspectionTypes={inspectionTypes}
          editingChecklist={editingChecklist}
          onAddItem={handleAddItem}
          onUpdateItem={handleUpdateItem}
          onRemoveItem={handleRemoveItem}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Checklist"
          message="Are you sure you want to delete this checklist? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
          onConfirm={handleDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setChecklistToDelete(null);
          }}
        />
      </>
    </AppLayout>
  );
};

export default Checklist;
